import { Link } from "react-router-dom";
import type { PersonProfile } from "@sales-ops-amplify/shared";

export function PersonOverviewTab({
  profile,
  companyHref,
}: {
  profile: PersonProfile;
  companyHref?: string;
}) {
  const org = profile.organization;
  const keywords = org.keywords?.slice(0, 8) ?? [];
  const technologies = org.technologies?.slice(0, 8) ?? [];

  return (
    <div className="min-w-0 max-w-full space-y-4 overflow-x-hidden">
      <section className="min-w-0 overflow-hidden rounded-lg border border-border bg-card p-4">
        <h3 className="text-sm font-semibold text-foreground">Person</h3>
        <dl className="mt-3 grid min-w-0 gap-3 sm:grid-cols-2">
          <div className="min-w-0">
            <dt className="text-[11px] uppercase text-muted-foreground">
              Title
            </dt>
            <dd className="break-words text-sm">{profile.title ?? "—"}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-[11px] uppercase text-muted-foreground">
              Seniority
            </dt>
            <dd className="break-words text-sm">{profile.seniority ?? "—"}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-[11px] uppercase text-muted-foreground">
              Departments
            </dt>
            <dd className="break-words text-sm">
              {profile.departments?.length
                ? profile.departments.join(", ")
                : "—"}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-[11px] uppercase text-muted-foreground">
              Location
            </dt>
            <dd className="break-words text-sm">
              {[profile.city, profile.state, profile.country]
                .filter(Boolean)
                .join(", ") || "—"}
            </dd>
          </div>
        </dl>
        {profile.qualification?.note ? (
          <p className="mt-3 break-words rounded-md bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            {profile.qualification.note}
          </p>
        ) : null}
        {profile.intent?.primaryTopic ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Intent: {profile.intent.primaryTopic}
            {profile.intent.primaryScore != null
              ? ` (${profile.intent.primaryScore})`
              : ""}
          </p>
        ) : null}
      </section>

      <section className="min-w-0 overflow-hidden rounded-lg border border-border bg-card p-4">
        <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="break-words text-sm font-semibold">
              {companyHref ? (
                <Link to={companyHref} className="hover:text-brand hover:underline">
                  {org.name}
                </Link>
              ) : (
                org.name
              )}
            </h3>
            <p className="mt-1 break-words text-xs text-muted-foreground">
              {[org.industry, org.employeeCount
                ? `${org.employeeCount.toLocaleString()} employees`
                : null]
                .filter(Boolean)
                .join(" · ") || "Company profile"}
            </p>
          </div>
          {companyHref ? (
            <Link
              to={companyHref}
              className="shrink-0 text-xs font-medium text-brand hover:underline"
            >
              Open company
            </Link>
          ) : null}
        </div>

        {org.description ? (
          <p className="mt-3 break-words text-sm leading-relaxed text-muted-foreground">
            {org.description}
          </p>
        ) : null}

        <dl className="mt-4 grid min-w-0 gap-3 sm:grid-cols-2">
          {org.annualRevenue != null ? (
            <div className="min-w-0">
              <dt className="text-[11px] uppercase text-muted-foreground">
                Revenue
              </dt>
              <dd className="break-words text-sm">{String(org.annualRevenue)}</dd>
            </div>
          ) : null}
          {org.companyAddress || org.companyCity ? (
            <div className="min-w-0">
              <dt className="text-[11px] uppercase text-muted-foreground">
                HQ
              </dt>
              <dd className="break-words text-sm">
                {org.companyAddress ||
                  [org.companyCity, org.companyState, org.companyCountry]
                    .filter(Boolean)
                    .join(", ")}
              </dd>
            </div>
          ) : null}
        </dl>

        {keywords.length > 0 ? (
          <div className="mt-4 min-w-0">
            <p className="mb-1.5 text-[11px] uppercase text-muted-foreground">
              Keywords
            </p>
            <div className="flex flex-wrap gap-1.5">
              {keywords.map((k) => (
                <span
                  key={k}
                  className="max-w-full break-words rounded-md border border-border bg-muted/30 px-2 py-0.5 text-[11px]"
                >
                  {k}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {technologies.length > 0 ? (
          <div className="mt-4 min-w-0">
            <p className="mb-1.5 text-[11px] uppercase text-muted-foreground">
              Technologies
            </p>
            <div className="flex flex-wrap gap-1.5">
              {technologies.map((t) => (
                <span
                  key={t}
                  className="max-w-full break-words rounded-md border border-border bg-muted/30 px-2 py-0.5 text-[11px]"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
